// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Slider } from '@advisor-app/components';
import { numberToFixed } from '@shared/ui-kit/utils';
import { useAppNavigate } from '@advisor-app/hooks';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useAppSelector } from '@advisor-app/store';
import { portfolioPositionsSlice } from '@shared/data-access/slices/portfolio-positions';
import { Button } from '@mui/material';
import { type BasePortfolio } from '@shared/data-access/interfaces/common/base-portfolio';
import { type Holding } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { Heading } from '@shared/ui-kit/components';
import { useCallback, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface AddCashProps {
  dataPortfolio: BasePortfolio;
  portfolioName?: string;
  to: Paths;
  onCancel?: () => void;
}

export const AddCash = ({
  dataPortfolio,
  portfolioName,
  to,
  onCancel,
}: AddCashProps) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const runSafeUpdate = useSafeUpdate();
  const { t } = useTranslation();

  const [investment, setInvestment] = useState(50000);

  const MAX_VAL = 10000000;

  const currency = useAppSelector(selectAccountCurrency);
  const prefix = useMemo(() => ({ USD: '$', EUR: '€' }[currency]), [currency]);

  const sendPortfolioToLoader = useCallback(() => {
    if (!dataPortfolio.ticker.length) {
      return;
    }

    const ticker: string[] = [];
    const holdings: Holding[] = [];
    dataPortfolio.ticker.forEach((symbol, idx) => {
      const outstandingBalance = numberToFixed(
        dataPortfolio.weight[idx] * investment,
        2,
      );
      const unrealizedCapitalGains = dataPortfolio.unrealized_capital_gains[idx];
      ticker.push(symbol);
      holdings.push({
        ticker: symbol,
        price: 1,
        qty: outstandingBalance,
        unrealized_capital_gains: unrealizedCapitalGains,
      });
    });

    runSafeUpdate(() => {
      dispatch(
        portfolioPositionsSlice.actions.setCurrentPortfolioPositions(holdings),
      );

      dispatch(
        portfolioPositionsSlice.actions.setPortfolioLoaded(portfolioName as string),
      );
    });

    navigate(to);
  }, [
    dataPortfolio.ticker,
    dataPortfolio.weight,
    dataPortfolio.unrealized_capital_gains,
    runSafeUpdate,
    navigate,
    to,
    investment,
    dispatch,
    portfolioName,
  ]);

  const onChangeInputValue = useCallback(
    (value: number) => setInvestment(value),
    [],
  );

  const onChangeCommitted = useCallback(
    (value: number) => setInvestment(value as number),
    [],
  );

  return (
    <div className="grid gap-6">
      <Heading
        title={t('dashboard.how-much-money-invest-add-cash', { ns: 'dashboard' })}
        variant="h2"
      />
      <div className="grid gap-4 rounded-lg bg-section p-6">
        <Heading
          variant="h3"
          title={t('dashboard.investment-add-cash', { ns: 'dashboard' })}
        />
        <NumericFormat
          value={investment}
          thousandSeparator={true}
          min={0}
          maxLength={11}
          isAllowed={({ value }) => {
            return Number(value) <= MAX_VAL;
          }}
          onValueChange={({ value }) => onChangeInputValue(Number(value))}
          prefix={prefix}
          placeholder="investment-input"
          className="input-common money-display"
          id="investment-input"
          data-testid="add-cash-modal-input"
        />
        <Slider
          valueLabelDisplay="off"
          onChange={(_, value) => onChangeCommitted(value as number)}
          onChangeCommitted={(_, value) => onChangeCommitted(value as number)}
          value={investment}
          min={5000}
          max={MAX_VAL}
          step={1000}
          data-testid="add-cash-modal-slider"
        />
      </div>
      <div className="flex justify-end gap-4">
        <Button
          variant="outlined"
          color="secondary"
          onClick={onCancel}
          data-testid="add-cash-cancel"
        >
          {t('common.cancel', { ns: 'common' })}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={sendPortfolioToLoader}
          data-testid="add-cash-next-id"
        >
          {t('dashboard.invest-add-cash', { ns: 'dashboard' })}
        </Button>
      </div>
    </div>
  );
};
